import { useEffect, useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";
import { ManufacturingEntityBulkBar } from "@/components/manufacturing/ManufacturingEntityBulkBar";
import {
  ghostBtnClass,
  mfgInlineInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import { slotLabelLinesToCsv } from "@/lib/manufacturing/slotLabelsCsvExport";
import {
  applyManufacturingSlotLabel,
  listManufacturingSlotCleanupLines,
  type SlotCleanupMaterialLine,
  type SlotLabelLineScope,
} from "@/lib/manufacturing/slotLabelCleanupClient";
import { SlotLabelTabCompleteInput } from "./SlotLabelTabCompleteInput";

const SCOPE_OPTIONS: Array<{ id: SlotLabelLineScope; label: string }> = [
  { id: "unlabeled", label: "Unlabeled" },
  { id: "labeled", label: "Labeled" },
  { id: "all", label: "All" },
];

export function ManufacturingSlotLabelsUnlabeledPanel({
  enabled,
  refreshTick,
  focusRequest = null,
  orgSlotLabels = [],
  onApplied,
}: {
  enabled: boolean;
  refreshTick: number;
  /** When set (new nonce), switch scope/search to show that label's materials. */
  focusRequest?: { scope: "all"; search: string; nonce: number } | null;
  orgSlotLabels?: readonly string[];
  onApplied?: () => void;
}) {
  const [lines, setLines] = useState<SlotCleanupMaterialLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<SlotLabelLineScope>("unlabeled");
  const [recipeFilterIds, setRecipeFilterIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [label, setLabel] = useState("");

  async function reload(nextScope: SlotLabelLineScope = scope) {
    if (!enabled) return;
    setBusy(true);
    setError(null);
    const r = await listManufacturingSlotCleanupLines(nextScope);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setLines(r.data.lines);
    setSelected([]);
    setRecipeFilterIds((prev) => {
      const valid = new Set(r.data.lines.map((ln) => ln.recipe_id));
      return prev.filter((id) => valid.has(id));
    });
  }

  useEffect(() => {
    void reload(scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refreshTick, scope]);

  useEffect(() => {
    if (!focusRequest) return;
    setScope(focusRequest.scope);
    setSearch(focusRequest.search);
    setRecipeFilterIds([]);
    setSelected([]);
  }, [focusRequest]);

  const recipeOptions = useMemo(() => {
    const map = new Map<string, { id: string; code: string; name: string }>();
    for (const ln of lines) {
      if (!map.has(ln.recipe_id)) {
        map.set(ln.recipe_id, {
          id: ln.recipe_id,
          code: ln.recipe_code,
          name: ln.recipe_name,
        });
      }
    }
    return [...map.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [lines]);

  const slotLabelSuggestions = useMemo(() => {
    const fromLines = lines.map((ln) => ln.slot_label).filter(Boolean);
    return [...new Set([...orgSlotLabels, ...fromLines])];
  }, [orgSlotLabels, lines]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lines.filter((ln) => {
      if (recipeFilterIds.length > 0 && !recipeFilterIds.includes(ln.recipe_id)) return false;
      if (!q) return true;
      return `${ln.material_code} ${ln.material_name} ${ln.recipe_code} ${ln.recipe_name} ${ln.slot_label}`
        .toLowerCase()
        .includes(q);
    });
  }, [lines, search, recipeFilterIds]);

  const filteredById = useMemo(() => new Map(filtered.map((ln) => [ln.line_id, ln])), [filtered]);

  const pager = usePagination(filtered, 10);

  useEffect(() => {
    pager.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, recipeFilterIds, scope]);

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleRecipeFilter(id: string) {
    setRecipeFilterIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function selectPage() {
    const ids = pager.paginatedItems.map((ln) => ln.line_id);
    setSelected((prev) => [...new Set([...prev, ...ids])]);
  }

  function selectAllFiltered() {
    setSelected(filtered.map((ln) => ln.line_id));
  }

  function exportLines(lineIds: string[]) {
    const rows = lineIds
      .map((id) => filteredById.get(id))
      .filter((ln): ln is SlotCleanupMaterialLine => Boolean(ln));
    if (rows.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`mfg-slot-label-lines-${stamp}.csv`, slotLabelLinesToCsv(rows));
  }

  function handleExportSelected() {
    setExportBusy(true);
    exportLines(selected);
    setExportBusy(false);
  }

  function handleExportAllFiltered() {
    setExportBusy(true);
    exportLines(filtered.map((ln) => ln.line_id));
    setExportBusy(false);
  }

  async function apply() {
    if (selected.length === 0 || !label.trim()) return;
    setSaveBusy(true);
    setError(null);
    const r = await applyManufacturingSlotLabel({
      line_ids: selected,
      slot_label: label,
    });
    setSaveBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setLabel("");
    await reload();
    onApplied?.();
  }

  const emptyCopy =
    scope === "unlabeled"
      ? "No unlabeled material lines."
      : scope === "labeled"
        ? "No labeled material lines."
        : "No material lines.";

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600 dark:text-zinc-400">
        Material-first view of BOM lines. Filter by recipe and label state, export assignments, or
        apply a slot label to many rows.
      </p>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/35 dark:text-red-200">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <select
          className={mfgInlineInputClass}
          value={scope}
          onChange={(e) => setScope(e.target.value as SlotLabelLineScope)}
          aria-label="Label scope"
        >
          {SCOPE_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          className={`${mfgInlineInputClass} min-w-[160px] flex-1`}
          placeholder="Search material or recipe…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <ManufacturingEntityBulkBar
        entityLabel="line"
        selectedCount={selected.length}
        filteredCount={filtered.length}
        allFilteredSelected={
          filtered.length > 0 && filtered.every((ln) => selected.includes(ln.line_id))
        }
        busy={exportBusy}
        onSelectAllFiltered={selectAllFiltered}
        onClear={() => setSelected([])}
        onExportCsv={handleExportSelected}
        onExportAllFiltered={handleExportAllFiltered}
        hint="CSV includes line_id for round-trip bulk import."
      />

      <details className="rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
        <summary className="cursor-pointer text-xs font-semibold text-gray-700 dark:text-zinc-300">
          Filter by recipe
          {recipeFilterIds.length > 0 ? ` (${recipeFilterIds.length} selected)` : ""}
        </summary>
        <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
          {recipeOptions.length === 0 ? (
            <div className="text-xs text-gray-500">No recipes in this scope.</div>
          ) : (
            recipeOptions.map((r) => (
              <label key={r.id} className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={recipeFilterIds.includes(r.id)}
                  onChange={() => toggleRecipeFilter(r.id)}
                />
                <span>
                  {r.code} — {r.name}
                </span>
              </label>
            ))
          )}
        </div>
        {recipeFilterIds.length > 0 ? (
          <button
            type="button"
            className={`${ghostBtnClass} mt-2`}
            onClick={() => setRecipeFilterIds([])}
          >
            Clear recipe filter
          </button>
        ) : null}
      </details>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={ghostBtnClass} onClick={selectPage}>
          Select page
        </button>
        <button type="button" className={ghostBtnClass} onClick={() => setSelected([])}>
          Clear
        </button>
        <span className="text-xs text-gray-500">{selected.length} selected</span>
      </div>

      <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
        {busy ? (
          <div className="py-4 text-center text-sm text-gray-500">Loading…</div>
        ) : null}
        {!busy &&
          pager.paginatedItems.map((ln) => (
            <label key={ln.line_id} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.includes(ln.line_id)}
                onChange={() => toggle(ln.line_id)}
              />
              <span>
                <span className="font-semibold">
                  {ln.quantity} × {ln.material_code || "?"} {ln.material_name}
                </span>
                <span className="block text-xs text-gray-500 dark:text-zinc-400">
                  {ln.recipe_code} — {ln.recipe_name}
                  {ln.slot_label ? ` · slot ${ln.slot_label}` : " · unlabeled"}
                </span>
              </span>
            </label>
          ))}
        {!busy && pager.paginatedItems.length === 0 ? (
          <div className="py-4 text-center text-sm text-gray-500">{emptyCopy}</div>
        ) : null}
      </div>

      <PaginationBar
        pageIndex={pager.pageIndex}
        pageCount={pager.pageCount}
        pageSize={pager.pageSize}
        totalCount={pager.totalCount}
        firstShown={pager.firstShown}
        lastShown={pager.lastShown}
        onPageChange={pager.setPageIndex}
        onPageSizeChange={pager.setPageSize}
        noun="lines"
        alwaysShow
      />

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-[180px] flex-1 flex-col gap-1.5">
          <span className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
            Slot label to apply
          </span>
          <SlotLabelTabCompleteInput
            value={label}
            onChange={setLabel}
            suggestions={slotLabelSuggestions}
            disabled={saveBusy || selected.length === 0}
            placeholder={
              selected.length === 0
                ? "Select materials first…"
                : "Type a label — Tab to autocomplete"
            }
          />
        </label>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={saveBusy || selected.length === 0 || !label.trim()}
          onClick={() => void apply()}
        >
          {saveBusy ? "Applying…" : `Apply to ${selected.length || 0}`}
        </button>
      </div>
    </div>
  );
}
